import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { ResponseGraphql } from 'shared/types/response-graphql';
import { parseResponseError } from 'shared/utils';
import { ActivePaidSubscriptionDataMapper } from '../data-mappers/active-paid-subscription.data-mapper';
import { OrganizerDataMapper } from '../data-mappers/organizer.data-mapper';
import { SocialNetDataMapper } from '../data-mappers/social-net.data-mapper';

import { ActivePaidSubscription } from '../interfaces/active-paid-subscription';
import { ActivePaidSubscriptionResponse } from '../interfaces/active-paid-subscription-response';
import { AuthAvailableFeatureResponse } from '../interfaces/available-feature-dto';
import { Feature } from '../interfaces/available-feature';
import { Organizer } from '../interfaces/organizer';
import { OrganizerInfoDto } from '../interfaces/organizer-dto';
import { SocialNet } from '../interfaces/social-net';
import { SocialNetResponseDto } from '../interfaces/social-net-dto';
import { UpsertOrganizerInput } from '../interfaces/upsert-organizer-input';
import { UpsertOrganizerResponse } from '../interfaces/upsert-organizer-response';
import { UpsertSocialNetInput, UpsertSocialNetResponseDto } from '../interfaces/upsert-social-net-input';

export class OrganizerRepository {
  constructor(private readonly graphqlResource: GraphqlResource) {}

  async fetchOrganizer(tournamentId: number): Promise<Organizer | null> {
    const query = `#graphql
    query getTournamentDetails($id: Int!) {
      tournament(id: $id) {
        id
        name
        sportType
        email
        phone
        description
        championships: championshipsPaginated(limit: 5, offset: 0) {
          id
          name
          startDate
          endDate
        }
        images {
          type
          externalUrl
          createdAt
        }
        address {
          city
          country
          region
        }
      }
    }
    `;

    const response = await this.graphqlResource.query<ResponseGraphql<{ tournament: OrganizerInfoDto }>>(query, { id: tournamentId });

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const dataMapper = new OrganizerDataMapper();
    return dataMapper.decode(response.data.data.tournament);
  }

  async upsertOrganizer(input: UpsertOrganizerInput): Promise<Organizer> {
    const query = `#graphql
    mutation upsertTournament($input: UpsertTournamentInput!) {
      upsertTournament(input: $input) {
        id
        name
        sportType
        email
        phone
        description
        championships: championshipsPaginated(limit: 5, offset: 0) {
          id
          name
          startDate
          endDate
        }
        images {
          type
          externalUrl
          createdAt
        }
        address {
          city
          country
          region
        }
      }
    }
    `;

    const variables = { input };
    const response = await graphqlResource.query<UpsertOrganizerResponse>(query, variables);
    const { data: result } = response;

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    const dataMapper = new OrganizerDataMapper();
    return dataMapper.decode(result.data.upsertTournament);
  }

  async fetchAvailableFeature(tournamentId: number): Promise<Feature> {
    const query = `#graphql
      query getMyFeatures($id: Int!) {
        features(tournamentId: $id) {
          feature
          isEnabled
        }
      }
    `;

    const variables = { id: tournamentId };
    const response = await graphqlResource.query<AuthAvailableFeatureResponse>(query, variables);
    const { data: result } = response;

    if (result?.errors) {
      throw new Error(parseResponseError(result.errors[0]));
    }

    const mappedFeatures: Feature = {
      MEDIA_MANAGER: false,
      ACTIVE_CHAMPIONSHIPS: false,
      OBS_INTEGRATION: false,
      JOYSTICK: false,
      TOURNAMENT_ANALYTICS: false,
      AI_CHAT_ASSISTANT: false,
    };

    result.data.features.forEach((loopFeature) => {
      const feature = loopFeature.feature;
      mappedFeatures[feature] = loopFeature.isEnabled;
    });

    return mappedFeatures;
  }

  async fetchActivePaidSubscription(tournamentId: number): Promise<ActivePaidSubscription | null> {
    const query = `#graphql
    query ActivePaidSubscription($tournamentId: Int!) {
      activePaidSubscription(tournamentId: $tournamentId) {
        id
        ownerId
        activeFrom
        activeTo
        purchaseType
      }
    }
    `;

    const response = await this.graphqlResource.query<ActivePaidSubscriptionResponse>(query, {
      tournamentId,
    });
    const { data: result } = response;

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    const dataMapper = new ActivePaidSubscriptionDataMapper();
    return dataMapper.decode(result.data.activePaidSubscription);
  }

  async fetchSocialNet(tournamentId: number): Promise<SocialNet[]> {
    const query = `#graphql
    query getOrganizerSocialMedias($tournamentId: Int!) {
      tournament(id: $tournamentId) {
        socialMedias {
          id
          type
          url
        }
      }
    }
    `;

    const response = await this.graphqlResource.query<SocialNetResponseDto>(query, {
      tournamentId,
    });
    const { data: result } = response;

    const socialNets: SocialNet[] = [];

    // Add regular social media entries
    if (result.data.tournament.socialMedias && result.data.tournament.socialMedias.length) {
      const dataMapper = new SocialNetDataMapper();
      socialNets.push(...dataMapper.decodeArray(result.data.tournament.socialMedias));
    }

    return socialNets;
  }

  async upsertSocialNet(input: UpsertSocialNetInput): Promise<SocialNet> {
    // Regular case for social media entries
    const query = `#graphql
    mutation upsertSocialMedia($input: UpsertSocialMediaInput!) {
      upsertSocialMedia(input: $input) {
        id
        type
        url
      }
    }
    `;

    const variables = { input };
    const response = await this.graphqlResource.query<UpsertSocialNetResponseDto>(query, variables);
    const { data: result } = response;

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    const dataMapper = new SocialNetDataMapper();
    return dataMapper.decode(result.data.upsertSocialMedia);
  }
}

const organizerRepository = new OrganizerRepository(graphqlResource);
export default organizerRepository;
